import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Newspaper, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function PressReleaseList() {
  const navigate = useNavigate();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReleases();
  }, []);

  const fetchReleases = async () => {
    try {
      const response = await axios.get(`${API}/api/press-releases`);
      setReleases(response.data.press_releases || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching press releases:", error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading press releases...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <Newspaper className="w-12 h-12 mr-3" />
          </div>
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Press Release Archive
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Latest news and announcements from Its-Sold.com™ - America's Original FSBO Marketplace since 1998
          </p>
        </div>
      </div>

      {/* Press Releases List */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        {releases.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl text-gray-600">No press releases yet. Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {releases.map((release) => (
              <Card 
                key={release.id} 
                className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border-l-4 border-blue-600"
                onClick={() => navigate(`/press-releases/${release.id}`)}
              >
                <div className="p-8">
                  {/* Date & Type */}
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(release.created_at)}
                    </span>
                    <span className="text-gray-500 text-sm">FOR IMMEDIATE RELEASE</span>
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 hover:text-blue-600 transition">
                    {release.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-gray-700 mb-4 line-clamp-3 text-lg leading-relaxed">
                    {release.content.substring(0, 300)}...
                  </p>

                  {/* Contact Info Preview */}
                  <div className="text-sm text-gray-600 mb-4 border-t pt-4">
                    <strong>Contact:</strong> Its-Sold.com™ Press Office
                  </div>

                  {/* Read More Button */}
                  <Button 
                    variant="ghost" 
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    Read Full Press Release
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Media Contact Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 py-12 mt-20">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Media Inquiries</h2>
          <p className="text-xl text-blue-100 mb-6">
            For press inquiries, interviews, or additional information, please contact:
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-left max-w-md mx-auto">
            <p className="mb-2"><strong>Company:</strong> Its-Sold.com™</p>
            <p className="mb-2"><strong>Phone:</strong> (877) 394-0210</p>
            <p className="mb-2"><strong>Address:</strong> 5624 Dyer St. Suite 601, Dallas, TX 75206</p>
            <p><strong>Website:</strong> https://its-sold.com</p>
          </div>
        </div>
      </div>
    </div>
  );
}
